import { IMainTableColumn } from "components/MainTable/MainTableInterfaces";
import { ICorporateDetailRow } from "../CorporationDetailsInterfaces";
import { ApolloQueryResult, OperationVariables } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { useCallback, useMemo } from "react";
import dayjs from "dayjs";
import { MillsEntity } from "entities/Mills/MillsEntity";
import MainSwitch from "components/MainSwitch/MainSwitch.view";
import { IOption } from "components/TableMenu/TableMenuInterfaces";
import { TableMenu } from "components";
import { useStyles } from "../CorporationDetails.styles";

interface ICorporationsDetailsColumns extends Omit<IMainTableColumn, "render"> {
    render: (row: ICorporateDetailRow) => string | JSX.Element | number | boolean | undefined;
}

export const useCorporationsDetailsColumns = (
    refetch: (variables?: Partial<OperationVariables> | undefined) => Promise<ApolloQueryResult<any>>,
): ICorporationsDetailsColumns[] => {
    const { t } = useTranslation();
    const classes = useStyles();
    const { updateEnableOrDisableMill } = MillsEntity.EnableOrDisableMill();
    const { activateClusterFeature, deactivateClusterFeature } = MillsEntity.UpdateMillClusterFeature();

    const renderClusterOptions = useCallback(
        (row: ICorporateDetailRow) => {
            if (!row.clusterEnabled) {
                return {
                    name: t("setup_corporation.corporation_mills.activate"),
                    callback: async () => {
                        row.id && (await activateClusterFeature(row.id));
                        await refetch();
                    },
                };
            } else {
                return {
                    name: t("setup_corporation.corporation_mills.deactivate"),
                    callback: async () => {
                        row.id && (await deactivateClusterFeature(row.id));
                        await refetch();
                    },
                };
            }
        },
        [refetch, t, activateClusterFeature, deactivateClusterFeature],
    );

    const tableColumns = useMemo(
        (): ICorporationsDetailsColumns[] => [
            {
                id: "name",
                label: t("setup_corporation.corporation_mills.table_name"),
                hideSortIcon: true,
                render: (row) => row.name,
            },
            {
                id: "location",
                label: t("setup_corporation.corporation_mills.table_location"),
                hideSortIcon: true,
                render: (row) => row.location,
            },
            {
                id: "division",
                label: t("setup_corporation.corporation_mills.table_division"),
                hideSortIcon: true,
                render: (row) => (row.division ? row.division : "N/A"),
            },
            {
                id: "region",
                label: t("setup_corporation.corporation_mills.table_region"),
                hideSortIcon: true,
                render: (row) => (row.region ? row.region : "N/A"),
            },
            {
                id: "admin",
                label: t("setup_corporation.corporation_mills.table_admin"),
                hideSortIcon: true,
                render: (row) => row.admin,
            },
            {
                id: "date",
                label: t("setup_corporation.corporation_mills.table_date_added"),
                hideSortIcon: true,
                render: (row) => dayjs(row.date).format("MM, DD, YYYY"),
            },
            {
                id: "module",
                label: t("setup_corporation.corporation_mills.table_module"),
                hideSortIcon: true,
                render: (row) => row.module,
            },
            {
                id: "enabled",
                label: t("setup_corporation.corporation_mills.table_enabled"),
                hideSortIcon: true,
                render: (row) => (
                    <MainSwitch
                        checked={row.enabled}
                        onChange={async () => {
                            await updateEnableOrDisableMill({
                                variables: {
                                    mill_id: row.id,
                                    is_enabled: !row.enabled,
                                },
                            });
                            refetch();
                        }}
                    />
                ),
            },
            {
                id: "account_status",
                label: t("setup_corporation.corporation_mills.account_status"),
                hideSortIcon: true,
                render: (row): any => (
                    <span className={classes.statusButton}>
                        {row.clusterEnabled ? t("setup_corporation.active") : t("setup_corporation.inactive")}
                    </span>
                ),
            },
            {
                id: "options",
                label: "",
                hideSortIcon: true,
                render: (row) => {
                    const options: IOption[] = [{ ...renderClusterOptions(row) }];
                    return <TableMenu options={options} />;
                },
            },
        ],
        [refetch, t, updateEnableOrDisableMill, classes.statusButton, renderClusterOptions],
    );

    return tableColumns;
};
